import React, { useState, useMemo } from 'react';
import {
  gregorianToEth,
  ethToGregorian,
  ETHIOPIAN_MONTH_OPTIONS,
} from '../utils/ethiopianCalendar';

/**
 * EthiopianDatePicker
 *
 * Shows three dropdowns (Day / Month / Year in Ethiopian calendar).
 * On change calls onDateChange(gregorianDateString) where
 * gregorianDateString is "YYYY-MM-DD".
 *
 * @param {string}   value        - current value as "YYYY-MM-DD" Gregorian
 * @param {function} onDateChange - callback(gregorianString)
 * @param {string}   label        - optional label text
 * @param {object}   style        - optional style overrides
 * @param {boolean}  disabled     - disable the picker
 * @param {string}   maxDate      - max Gregorian date "YYYY-MM-DD"
 */
export const EthiopianDatePicker = ({
  value,
  onDateChange,
  label,
  disabled = false,
  maxDate,
  className = "",
}) => {

  // ── Convert current Gregorian value → Ethiopian ──────────
  const currentEth = useMemo(() => {
    if (!value) return null;
    try { return gregorianToEth(new Date(value + "T12:00:00")); }
    catch { return null; }
  }, [value]);

  // ── Local state for the three dropdowns ───────────────────
  const todayEth = useMemo(() => gregorianToEth(new Date()), []);

  const [ethDay,   setEthDay]   = useState(currentEth?.day   || todayEth.day);
  const [ethMonth, setEthMonth] = useState(currentEth?.month || todayEth.month);
  const [ethYear,  setEthYear]  = useState(currentEth?.year  || todayEth.year);

  // ── Year options — current year ± 10 ─────────────────────
  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = todayEth.year + 1; y >= todayEth.year - 10; y--) {
      years.push(y);
    }
    return years;
  }, [todayEth.year]);

  // ── Days in selected Ethiopian month ─────────────────────
  // Months 1–12 = 30 days, month 13 (Pagume) = 5 or 6
  const daysInMonth = useMemo(() => {
    if (ethMonth === 13) {
      // Pagume: 6 days in Ethiopian leap year (every 4 years), else 5
      return ethYear % 4 === 3 ? 6 : 5;
    }
    return 30;
  }, [ethMonth, ethYear]);

  // ── Convert and call parent ───────────────────────────────
  const handleChange = (day, month, year) => {
    const safeDay = Math.min(day, month === 13
      ? (year % 4 === 3 ? 6 : 5)
      : 30);

    try {
      const gcDate = ethToGregorian(year, month, safeDay);
      const gcStr  = gcDate.toISOString().split("T")[0];

      // Respect maxDate
      if (maxDate && gcStr > maxDate) return;

      onDateChange(gcStr);
    } catch (e) {
      console.warn("EthiopianDatePicker conversion error:", e);
    }
  };

  const onDay = (e) => {
    const d = Number(e.target.value);
    setEthDay(d);
    handleChange(d, ethMonth, ethYear);
  };

  const onMonth = (e) => {
    const m = Number(e.target.value);
    setEthMonth(m);
    // Clamp day if month has fewer days
    const maxDay = m === 13 ? (ethYear % 4 === 3 ? 6 : 5) : 30;
    const clampedDay = Math.min(ethDay, maxDay);
    setEthDay(clampedDay);
    handleChange(clampedDay, m, ethYear);
  };

  const onYear = (e) => {
    const y = Number(e.target.value);
    setEthYear(y);
    handleChange(ethDay, ethMonth, y);
  };

  // ── Display the converted GC date ─────────────────────────
  const gcDisplay = useMemo(() => {
    if (!value) return "";
    try {
      return new Date(value + "T12:00:00").toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric"
      });
    } catch { return ""; }
  }, [value]);

  const selectStyle = {
    border:       "1px solid #ced4da",
    borderRadius: "0.375rem",
    padding:      "0.5rem",
    fontSize:     "1.4rem",
    background:   "white",
    cursor:       disabled ? "not-allowed" : "pointer",
  };

  return (
    <div className={className}>
      {label && (
        <label className="form-label fs-4 fw-medium mb-1 d-block">
          {label}
        </label>
      )}

      <div className="d-flex gap-2 align-items-center flex-wrap">

        {/* Day */}
        <div>
          <label className="text-muted d-block mb-1"
            style={{ fontSize: "0.75rem" }}>
            Day
          </label>
          <select
            value={ethDay}
            onChange={onDay}
            disabled={disabled}
            style={{ ...selectStyle, width: "70px" }}>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        {/* Month */}
        <div>
          <label className="text-muted d-block mb-1"
            style={{ fontSize: "0.75rem" }}>
            Month
          </label>
          <select
            value={ethMonth}
            onChange={onMonth}
            disabled={disabled}
            style={{ ...selectStyle, width: "130px" }}>
            {ETHIOPIAN_MONTH_OPTIONS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        {/* Year */}
        <div>
          <label className="text-muted d-block mb-1"
            style={{ fontSize: "0.75rem" }}>
            Year (E.C.)
          </label>
          <select
            value={ethYear}
            onChange={onYear}
            disabled={disabled}
            style={{ ...selectStyle, width: "110px" }}>
            {yearOptions.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {/* Gregorian equivalent — read only display */}
        {gcDisplay && (
          <div style={{ paddingTop: "18px" }}>
            <span className="badge bg-light text-muted border fs-5 px-3 py-2">
              📅 {gcDisplay}
            </span>
          </div>
        )}

      </div>

      {/* Hidden native input — for form validation if needed */}
      <input
        type="hidden"
        value={value || ""}
        readOnly
      />
    </div>
  );
};

/**
 * EthiopianDateRangePicker — two EthiopianDatePickers side by side
 *
 * @param {string}   startDate        - "YYYY-MM-DD"
 * @param {string}   endDate          - "YYYY-MM-DD"
 * @param {function} onStartChange    - callback(gcString)
 * @param {function} onEndChange      - callback(gcString)
 */
export const EthiopianDateRangePicker = ({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
}) => {
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="row g-3">
      <div className="col-12 col-md-6">
        <EthiopianDatePicker
          label="Start Date (Ethiopian)"
          value={startDate}
          onDateChange={onStartChange}
          maxDate={endDate || today}
        />
      </div>
      <div className="col-12 col-md-6">
        <EthiopianDatePicker
          label="End Date (Ethiopian)"
          value={endDate}
          onDateChange={onEndChange}
          maxDate={today}
        />
      </div>
    </div>
  );
};